import { formatCurrency } from '@adornis/accounting/base/api/currencyFormat.js';
import { AccoutingEntryDirection } from '@adornis/accounting/base/entities/AccountingEntry.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import '@adornis/filter/client/x-filter-editor.js';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import { stateful, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import '@adornis/popover/x-tooltip.js';
import { confirmDialog } from '@adornis/dialog/dialog-functions.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { XViewManager } from '@adornis/view-manager/client/x-view-manager.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { format } from 'date-fns';
import { html } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { AccountingResolutionAPI } from '../../accounting-resolution/api/AccountingResolutionAPI.js';
import {
  subscribeUserBasedViewProfiles,
  UserBasedViewProfile,
} from '../../view-manager/subscribeUserBasedViewProfiles.js';
import { UserExport } from '../../users/export/UserExport.js';
import { MartinusAccountingEntry } from '../entities/MartinusAccountingEntry.js';

/**
 * Every field named here is regex-scanned on *every* document the sort walks over, so a field that
 * carries no data costs a full scan and can never contribute a hit. `debitorennummer` was such a
 * field - it exists on zero accounting entries.
 */
const SEARCH_FIELDS = ['transactionText', 'processIdentifier', 'belegfeld1', 'kontoinhaber', 'iban'];

const directionLabel: Record<AccoutingEntryDirection, string> = {
  [AccoutingEntryDirection.SOLL]: 'S',
  [AccoutingEntryDirection.HABEN]: 'H',
  [AccoutingEntryDirection.UNDETERMINED]: '–',
};

const cell = (content: unknown) => html` <div class="py-2 px-4 min-w-0 truncate">${content}</div> `;

/**
 * How the accounting fields render. This is only the *how* - which columns are visible out of the
 * box, and in which order, is `DEFAULT_COLUMN_FIELDS` below. Everything else on the entity stays
 * reachable through the column picker.
 */
const columns: ColumnDefinition<typeof MartinusAccountingEntry>[] = [
  {
    fieldName: 'timestamp',
    columnName: 'Datum',
    columnWidth: '110px',
    renderItem: entry => cell(entry.timestamp ? formatCalendarDay(entry.timestamp, 'dd.MM.yyyy') : ''),
  },
  { fieldName: 'fiscalYear', columnName: 'WJ', columnWidth: '90px' },
  {
    fieldName: 'direction',
    columnName: 'S/H',
    columnWidth: '70px',
    renderItem: entry => cell(directionLabel[entry.direction ?? AccoutingEntryDirection.UNDETERMINED]),
  },
  {
    fieldName: 'amountInMinorUnit',
    columnName: 'Betrag',
    columnWidth: '130px',
    // the currency decides the minor-unit factor, so it has to come along even though it has no column
    selectionSet: { amountInMinorUnit: 1, currency: 1 },
    renderItem: entry => html`
      <div
        class="py-2 px-4 min-w-0 truncate text-right tabular-nums ${(entry.amountInMinorUnit ?? 0) < 0 ? 'text-error' : ''}"
      >
        ${formatCurrency(entry.amountInMinorUnit, entry.currency)}
      </div>
    `,
  },
  { fieldName: 'account', columnName: 'Konto', columnWidth: '110px' },
  { fieldName: 'counterPartyAccount', columnName: 'Gegenkonto', columnWidth: '120px' },
  { fieldName: 'accountingCategory', columnName: 'Kategorie', columnWidth: '130px' },
  { fieldName: 'kostenstelle', columnName: 'KOST1', columnWidth: '100px' },
  { fieldName: 'kostentraeger', columnName: 'KOST2', columnWidth: '100px' },
  { fieldName: 'belegfeld1', columnName: 'Belegfeld 1', columnWidth: '150px' },
  { fieldName: 'processIdentifier', columnName: 'Vorgang', columnWidth: '190px' },
  { fieldName: 'transactionText', columnName: 'Buchungstext', columnWidth: 'minmax(280px, 1fr)' },
  {
    fieldName: 'schools',
    columnName: 'Schulen',
    columnWidth: '180px',
    // the schools are loaded from `schoolIDs`, which therefore has to be queried along with them
    selectionSet: { schoolIDs: 1, schools: { name: 1 } },
    renderItem: entry => cell(entry.schools?.map(school => school.name).join(', ')),
  },
  { fieldName: 'source', columnName: 'Quelldatei', columnWidth: '220px' },
  { fieldName: 'importError', columnName: 'Importfehler', columnWidth: '200px' },
  { fieldName: 'kontoinhaber', columnName: 'Kontoinhaber', columnWidth: '240px' },
  { fieldName: 'iban', columnName: 'IBAN', columnWidth: '220px' },
];

/** shown on startup - the accounting data itself, rather than an empty table waiting to be configured */
const DEFAULT_COLUMN_FIELDS = [
  'timestamp',
  'fiscalYear',
  'direction',
  'amountInMinorUnit',
  'account',
  'counterPartyAccount',
  'accountingCategory',
  'kostenstelle',
  'kostentraeger',
  'belegfeld1',
  'processIdentifier',
  'transactionText',
  'schools',
  'source',
  'importError',
];

const makeDefaultView = () =>
  new View<typeof MartinusAccountingEntry>({
    filter: new AdornisFilter({
      searchFields: SEARCH_FIELDS,
      sortingFields: ['timestamp'],
      sortingDirections: ['desc'],
    }),
    configuredColumns: DEFAULT_COLUMN_FIELDS.map(fieldName => {
      const columnWidth = columns.find(col => col.fieldName === fieldName)?.columnWidth;
      return new FieldConfiguration({ fieldName, columnWidth });
    }),
  });

/**
 * Who a booking belongs to is resolved school by school (two of the rules depend on the order of all rows of a
 * school), so recalculating one booking means recalculating its school. It runs in the background; the table
 * is live and updates once the worker is through.
 */
async function recalculateReferences(entryID: string) {
  const confirmed = await confirmDialog(
    html`
      <div class="flex flex-col gap-2">
        <span class="font-bold">Zuordnung dieser Buchung neu berechnen?</span>
        <span>
          Personen, Kontoinhaber und Vorgang werden für alle Buchungen der Schule neu ermittelt. Das dauert bis zu einer
          Minute und läuft im Hintergrund.
        </span>
      </div>
    `,
    { acceptText: 'Neu berechnen', declineText: 'Abbrechen' },
  );
  if (!confirmed) return;

  await AccountingResolutionAPI.recalculateEntry(entryID)();
  SgSnackbar.show('Die Zuordnung wird neu berechnet');
}

export const SgQuittungenDataView = stateful(() => {
  const [view, setView] = useState<View<typeof MartinusAccountingEntry> | null>(null);
  const defaultView = useMemo(() => [makeDefaultView()], []);
  const activeView = view ?? defaultView;

  // Saved views persist their own searchFields, and profiles from earlier field namings still carry
  // fields the entity no longer has - searching those scans everything to find nothing. Only write
  // when it actually differs: assigning publishes on the filter's change stream, and an
  // unconditional write would reload the data on every render without ever settling.
  if (activeView.filter && activeView.filter.searchFields?.join() !== SEARCH_FIELDS.join())
    activeView.filter.searchFields = [...SEARCH_FIELDS];

  // same for the columns: the school names used to be a stored field, a view saved back then still asks for it
  const storedNamesColumn = activeView.configuredColumns?.find(column => column.fieldName === 'schoolNames');
  if (storedNamesColumn) storedNamesColumn.fieldName = 'schools';

  // rebuilt only when the view is actually swapped: buildFullOptions returns a fresh object every
  // call, and the data table keys its subscription on that identity - rebuilding per render would
  // resubscribe (and reset the pagination) on every keystroke
  const options = useMemo(
    () => [
      buildFullOptions(
        {
          class: MartinusAccountingEntry,
          isColumnSelectable: true,
          isColumnResortable: true,
          isColumnResizable: true,
          customColumns: columns,
          view: activeView,
          onViewChanged: newView => setView(newView),
          entryClickHandler: entry => {
            void recalculateReferences(entry._id).catch(error => SgSnackbar.show(error.message));
          },
        } satisfies DataViewOptions<typeof MartinusAccountingEntry>,
        {
          searchable: true,
          fieldAspectGeneration: { class: MartinusAccountingEntry },
        } satisfies Omit<FilterEditorOptions<typeof MartinusAccountingEntry>, 'class' | 'filter'>,
      ),
    ],
    [activeView],
  );

  const user = useObservable(() => currentUser(), []);

  return html`
    <div class="flex flex-col h-full overflow-y-auto">
      <div class="card card-body flex-col m-4 sticky z-10">
        ${FilterEditor({
          noCollapse: true,
          fullWidth: true,
          options: options.filterOptions,
          additionalButtons: () => html`
            <div class="flex gap-4 items-start">
              ${XViewManager({
                category: 'admin-quittungen',
                operations: {
                  transformViewBeforeCreate: viewProfile => {
                    const newViewProfile = new UserBasedViewProfile(viewProfile);

                    newViewProfile.userId = user?._id;
                    return newViewProfile;
                  },
                  subscribeViews: subscribeUserBasedViewProfiles,
                },
                defaultView,
                viewChanged: view => {
                  setView(view as View<typeof MartinusAccountingEntry>);
                  if (view._id)
                    view
                      .save()
                      .then(() => SgSnackbar.show('Ansicht gespeichert'))
                      .catch(error => SgSnackbar.show(error.message));
                },
              })}
              <button
                class="btn btn-primary"
                @click=${() =>
                  exportDataView(options.dataViewOptions, {
                    fileName: `Export_${format(new Date(), 'yyyy-LL-dd-HH-mm')}`,
                    exportClass: UserExport,
                  })}
              >
                <span class="icon-[ic--baseline-cloud-download] text-base"></span>
                Export
              </button>
            </div>
          `,
        })}
      </div>
      <div class="flex flex-col overflow-y-auto h-full bg-base-100">
        ${DataTable({ options: options.dataViewOptions })}
      </div>
    </div>
  `;
});
